import type { Session } from "next-auth";

/**
 * Seats: which id means "the person". `session.user.id` is the ACCOUNT a person acts
 * inside; `session.user.actorId` is the person. Identity surfaces (terms, email, password,
 * verification, analytics) use the person. Never fall back from one to the other (Rule 1).
 */
export function getActorId(session: Session | null | undefined): string {
  const id = session?.user?.actorId;
  if (typeof id !== "string" || id.length === 0) throw new Error("session has no actorId");
  return id;
}

/**
 * PostHog and Sentry identity: the PERSON is the identified user, the account they act
 * inside rides as a property. Identifying by the account would let a team member's name
 * and email overwrite the owner's PostHog person and merge their replays.
 */
export function analyticsIdentity(
  session: Session | null | undefined,
): { personId: string; accountId: string; role: "owner" | "member" } | null {
  const u = session?.user;
  if (!u?.id || !u?.actorId) return null;
  return { personId: u.actorId, accountId: u.id, role: u.actorId === u.id ? "owner" : "member" };
}

/** A rep (team member) is a session whose person differs from its account. */
export function isRep(session: Session | null | undefined): boolean {
  const u = session?.user;
  return !!u?.id && !!u?.actorId && u.actorId !== u.id;
}

/**
 * Seats D: the Prisma `where` fragment that keeps a team member inside their own contacts
 * (decision 14: everything on a contact assigned to them, nothing on any other). Spread it into
 * every Property lookup a listed `direct` handler makes. An owner gets `{}` — their queries are
 * unchanged.
 */
export function memberPropertyWhere(session: Session | null | undefined): { assignedToUserId: string } | Record<string, never> {
  return isRep(session) ? { assignedToUserId: session!.user!.actorId as string } : {};
}

/** The person is the account itself. Strict: a missing or still-loading session is never the owner. */
export function isAccountOwnerSession(session: Session | null | undefined): boolean {
  const u = session?.user;
  return typeof u?.id === "string" && typeof u?.actorId === "string" && u.actorId === u.id;
}

/**
 * Seats R1: a session never changes account. tenantUserId is written once (attach), so a
 * stored account that differs from the row's current one means this person was attached
 * while signed in. A token minted before tenantId existed (pre seats PR 1) was the person's
 * own account: it counts as `sub`, so an owner's is unchanged and an attached person's ends.
 */
export function sessionAccountChanged(token: { tenantId?: unknown; sub?: string | null }, row: { tenantUserId: string | null }): boolean {
  const stored = typeof token.tenantId === "string" ? token.tenantId : token.sub;
  return stored !== (row.tenantUserId ?? token.sub);
}
